import React, { useMemo, useState, useEffect } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import { Card } from 'react-bootstrap'
import { formatDateOnly } from '../utils/commonUtils'

const InventoryStatusGraph = ({ data }) => {
    const [isDarkMode, setIsDarkMode] = useState(false)

    useEffect(() => {
        // Check if dark mode is active
        const checkDarkMode = () => {
            const htmlElement = document.documentElement
            const theme = htmlElement.getAttribute('data-bs-theme')
            setIsDarkMode(theme === 'dark')
        }

        checkDarkMode()
        
        // Listen for theme changes
        const observer = new MutationObserver(checkDarkMode)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })
        
        return () => {
            observer.disconnect()
        }
    }, [])
    const chartData = useMemo(() => {
        if (!data?.current_inventory?.by_location) return []

        return data.current_inventory.by_location.map(loc => ({
            location: `${loc.location} (${loc.warehouse})`,
            cases: loc.quantity || loc.cases,  // Use raw quantity for graph (includes pallet/film)
            warehouseName: loc.warehouse
        }))
    }, [data])

    const warehouses = useMemo(() => {
        if (!data?.current_inventory?.by_location) return new Set()
        return new Set(data.current_inventory.by_location.map(loc => loc.warehouse))
    }, [data])

    const warehouseColors = useMemo(() => {
        const colors = {
            'Industrial': '#2196F3',
            'Laeta': '#4CAF50',
        }
        return Array.from(warehouses).reduce((acc, warehouse, index) => {
            if (!colors[warehouse]) {
                const schemes = ['#FFA726', '#EF5350', '#AB47BC', '#EC407A', '#7E57C2']
                colors[warehouse] = schemes[index % schemes.length]
            }
            return colors
        }, {})
    }, [warehouses])

    // Calculate total raw quantity (cases) from location data
    // This is needed for pallet/film items where total_cases is 0
    const totalRawQuantity = useMemo(() => {
        if (!data?.current_inventory?.by_location) return 0
        return data.current_inventory.by_location.reduce((sum, loc) => {
            return sum + (loc.quantity || loc.cases || 0)
        }, 0)
    }, [data])

    if (!data?.current_inventory?.by_location) {
        return null
    }

    const totals = data.current_inventory.total

    // Build inventory summary based on which metrics are present
    const buildInventorySummary = () => {
        const parts = []

        // Add items if present
        if (totals.total_items > 0) {
            parts.push(`${totals.total_items} items`)
        }

        // Add cases - use total_cases for standard items, or raw quantity for pallet/film
        const casesCount = totals.total_cases > 0 ? totals.total_cases : totalRawQuantity
        if (casesCount > 0) {
            parts.push(`${casesCount} cases`)
        }

        // Add weight if present
        if (totals.total_weight > 0) {
            parts.push(`${totals.total_weight} Lbs`)
        }

        // Add pallets if present (in addition to cases)
        if (totals.total_pallets > 0) {
            parts.push(`${totals.total_pallets} pallets`)
        }

        // Add impressions (film) if present (in addition to cases)
        if (totals.total_film > 0) {
            parts.push(`${totals.total_film} impressions`)
        }

        // Add clear film rolls if present
        if (totals.total_clear_film > 0) {
            parts.push(`${totals.total_clear_film} rolls`)
        }

        return parts.length > 0 ? parts.join(' • ') : 'No inventory'
    }

    return (
        <Card className="mb-4">
            <Card.Header>
                <h4 className="mb-0">Current Inventory by Location</h4>
                <div className="text-muted mt-1">
                    <div className="fw-bold">
                        Total Inventory: {buildInventorySummary()}
                    </div>
                    {data.pending_orders && data.pending_orders.length > 0 && (
                        <div className="mt-2">
                            <span className="fw-bold">Pending Orders: </span>
                            {data.pending_orders.map((order, index) => (
                                <div key={`${order.po_number}-${index}`} className="ms-3 mt-1">
                                    <small>
                                        PO #{order.po_number} ({order.vendor}) - {order.cases} cases
                                        {order.desired_delivery_date ?
                                            ` • Delivery: ${formatDateOnly(order.desired_delivery_date)}` :
                                            order.desired_delivery_text ?
                                            ` • ${order.desired_delivery_text}` :
                                            ''}
                                    </small>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card.Header>
            <Card.Body>
                <div style={{ height: '400px', width: '100%' }}>
                    <ResponsiveBar
                        data={chartData}
                        keys={['cases']}
                        indexBy="location"
                        margin={{ top: 50, right: 130, bottom: 80, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={({ data }) => warehouseColors[data.warehouseName]}
                        borderColor={{
                            from: 'color',
                            modifiers: [['darker', 1.6]]
                        }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: -45,
                            legend: 'Location',
                            legendPosition: 'middle',
                            legendOffset: 60,
                            tickColor: isDarkMode ? '#ffffff' : '#000000',
                            legendColor: isDarkMode ? '#ffffff' : '#000000',
                            format: value => value,
                            renderTick: ({ x, y, value, tickIndex, format }) => (
                                <g transform={`translate(${x},${y})`}>
                                    <text
                                        x={0}
                                        y={0}
                                        dy={16}
                                        textAnchor="end"
                                        fill={isDarkMode ? '#ffffff' : '#000000'}
                                        fontSize={11}
                                        transform="rotate(-45)"
                                    >
                                        {value}
                                    </text>
                                </g>
                            )
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Cases',
                            legendPosition: 'middle',
                            legendOffset: -40,
                            tickColor: isDarkMode ? '#ffffff' : '#000000',
                            legendColor: isDarkMode ? '#ffffff' : '#000000',
                            renderTick: ({ x, y, value, tickIndex, format }) => (
                                <g transform={`translate(${x},${y})`}>
                                    <text
                                        x={-10}
                                        y={0}
                                        textAnchor="end"
                                        fill={isDarkMode ? '#ffffff' : '#000000'}
                                        fontSize={11}
                                    >
                                        {value}
                                    </text>
                                </g>
                            )
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={isDarkMode ? '#ffffff' : '#000000'}
                        legends={[
                            {
                                dataFrom: 'indexes',
                                anchor: 'bottom-right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 2,
                                itemWidth: 100,
                                itemHeight: 20,
                                itemDirection: 'left-to-right',
                                itemOpacity: 0.85,
                                symbolSize: 20,
                                itemTextColor: isDarkMode ? '#ffffff' : '#000000',
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemOpacity: 1
                                        }
                                    }
                                ],
                                data: Array.from(warehouses).map(warehouse => ({
                                    id: `${warehouse}`,
                                    label: warehouse,
                                    color: warehouseColors[warehouse]
                                }))
                            }
                        ]}
                        role="application"
                        tooltip={({ value, indexValue, color }) => (
                            <div
                                style={{
                                    padding: 12,
                                    background: isDarkMode ? '#2d3748' : '#ffffff',
                                    border: `2px solid ${color}`,
                                    borderRadius: '4px',
                                    color: isDarkMode ? '#ffffff' : '#000000',
                                    boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <strong>{indexValue}</strong><br />
                                Cases: {value}
                            </div>
                        )}
                    />
                </div>
            </Card.Body>
        </Card>
    )
}

export default InventoryStatusGraph 